/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { useEffect, useRef } from "react";

import cn from "classnames";
import { useOnClickOutside } from "usehooks-ts";

import { throttle } from "hooks/useThrottle";

import s from "../Dropdown.module.css";
const INITIAL_MAX_HEIGHT = 350;

interface IMenu {
  children:
    | React.ReactNode
    | ((onClose: (isOpen?: boolean) => void) => React.ReactNode);
  minHeight?: number;
  className?: string;
  hasShadow?: boolean;
  hasPadding?: boolean;
  pos?: "left" | "right" | "top";
  variant?: "dark" | "light";
  as?: React.ElementType;
  initialMaxHeight?: number;
  isOpen: boolean;
  onClose?: (isOpen?: boolean) => void;
  onClick?: (...args: any[]) => void;
  onScrollEnd?: () => void;
  isFetchingMore?: boolean;
  scrollable?: boolean;
  containerRef?: React.RefObject<HTMLElement>;
  hasMenuFullWidth?: boolean;
  shouldCalculateMaxHeight?: boolean;
  onClickOutside?: () => void;
}

const Menu = ({
  children,
  minHeight,
  className,
  hasShadow,
  hasPadding = true,
  pos = "left",
  variant = "dark",
  as: Component = "ul",
  initialMaxHeight = INITIAL_MAX_HEIGHT,
  isOpen,
  onClose,
  onScrollEnd,
  containerRef,
  isFetchingMore,
  scrollable,
  onClick,
  hasMenuFullWidth = false,
  shouldCalculateMaxHeight = true,
  onClickOutside,
}: IMenu) => {
  const menuRef = useRef<HTMLElement>(null);
  useOnClickOutside(containerRef || menuRef, () => {
    if (onClickOutside) return onClickOutside();
    if (isOpen && onClose) onClose(false);
  });

  const rootClass = cn(
    s.root,
    s.test,
    variant && [s[variant]],
    pos && [s[pos]],
    hasShadow && s.hasShadow,
    hasMenuFullWidth && s.start,
    className,
  );

  const maxHeight = useMenuHeightCalculation({
    isOpen,
    initialMaxHeight,
    minHeight,
    shouldCalculateMaxHeight,
    // @ts-ignore
    menuElement: menuRef.current,
  });

  const loadMore = () => {
    const elementTarget = menuRef.current;
    if (!elementTarget) return;
    const hasScrollReachedBottom =
      elementTarget.scrollTop + elementTarget.offsetHeight >=
      elementTarget.scrollHeight;

    if (hasScrollReachedBottom && !isFetchingMore && onScrollEnd) {
      onScrollEnd();
    }
  };

  useEffect(() => {
    const ref = menuRef.current;
    if (onScrollEnd) {
      const throttledLoadMore = throttle(loadMore, 500, isFetchingMore);
      menuRef.current?.addEventListener("scroll", throttledLoadMore);
      return () => {
        ref?.removeEventListener("scroll", throttledLoadMore);
      };
    }
  }, []);

  return (
    <Component
      ref={menuRef}
      className={cn(rootClass, "list-unstyled z-[2] !h-auto !space-y-0", {
        "p-0": isOpen && hasPadding,
        "!border-none": !isOpen,
        "overflow-auto": scrollable,
      })}
      style={
        isOpen ? { minHeight, maxHeight } : { maxHeight: 0, border: "none" }
      }
      onClick={(e: any) => {
        if (isOpen) {
          throttle(loadMore, 500, isFetchingMore)();
        }

        if (onClick) {
          onClick(e, menuRef.current);
        }
      }}
      test-id="NW2k0f2t"
    >
      {typeof children === "function" && onClose ? children(onClose) : children}
    </Component>
  );
};

interface IMenuHeightCalculation {
  isOpen: boolean;
  initialMaxHeight: number;
  minHeight?: number;
  menuElement: HTMLElement;
  shouldCalculateMaxHeight: boolean;
}

const useMenuHeightCalculation = ({
  isOpen,
  initialMaxHeight,
  minHeight = 0,
  menuElement,
  shouldCalculateMaxHeight,
}: IMenuHeightCalculation) => {
  const isFirstRender = useRef(true);
  const maxHeightRef = useRef(initialMaxHeight);
  useEffect(() => {
    if (menuElement && !isFirstRender.current && shouldCalculateMaxHeight) {
      if (maxHeightRef.current < minHeight) {
        maxHeightRef.current = minHeight;
      }

      if (
        (maxHeightRef.current > menuElement.offsetHeight &&
          maxHeightRef.current !== initialMaxHeight) ||
        menuElement.offsetHeight < minHeight
      ) {
        // Someone collapsed the menu too early! The offsetHeight is not full.
        return;
      }

      maxHeightRef.current = menuElement.offsetHeight;
    }

    if (isOpen && isFirstRender.current) {
      isFirstRender.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return maxHeightRef.current;
};

export default Menu;
